import React, { useEffect, useState } from 'react'
import FocusLock from 'react-focus-lock'
import Portal from '../Portal/Portal'
import { keyCode } from '../../util/constants'
import { Button, IconButton, TdsProvider } from '@instacart/tds'
import { clsx } from 'clsx'
import styles from './TDSDialog.module.scss'
import { CloseIcon, IdsProvider } from '@instacart/ids-core'

type TDSDialogBaseProps = {
  toggleDialog: () => void
  header: string
  children?: any
  cancelButtonLabel?: string
  cancelAction?: () => void
  customClass?: string
  autoFocus?: boolean
  showCloseIcon?: boolean
  closeButtonVariant?: 'primary' | 'secondary'
}

type TDSDialogWithAction = TDSDialogBaseProps & {
  action: (actionFinished: () => void) => void
  actionButtonLabel: string
  actionDisabled?: boolean
}

type TDSDialogWithoutAction = TDSDialogBaseProps & {
  action?: never
  actionButtonLabel?: never
  actionDisabled?: never
}

type TDSDialogProps = TDSDialogWithAction | TDSDialogWithoutAction

// TEMP duplicate of admin Dialog.tsx but with TDS styles until the TDS.Modal is available
export default function TDSDialog({
  toggleDialog,
  header,
  actionButtonLabel,
  cancelButtonLabel,
  cancelAction,
  customClass,
  children,
  action,
  actionDisabled,
  autoFocus,
  showCloseIcon = true,
  closeButtonVariant = 'secondary',
}: TDSDialogProps) {
  const [actionInProgress, setActionInProgress] = useState(false)
  const doCallback = (e: any) => {
    if (!action) return
    e.preventDefault()
    e.stopPropagation() // prevent form submit event
    setActionInProgress(true)
    action(() => {
      setActionInProgress(false)
    })
  }

  const keyEventListener = (event: KeyboardEvent) => {
    // Close modal when hitting the esc key
    if (event && event.code == keyCode.Escape && toggleDialog) {
      toggleDialog()
    }
  }

  useEffect(() => {
    document.addEventListener('keyup', keyEventListener)
    document.querySelector('body')?.classList.add('no-scroll')

    return () => {
      document.removeEventListener('keyup', keyEventListener)
      document.querySelector('body')?.classList.remove('no-scroll')
    }
  }, [keyEventListener])

  return (
    <Portal>
      <FocusLock autoFocus={autoFocus ?? true}>
        <TdsProvider>
          <IdsProvider>
            <div className={clsx(styles.reactDialog, styles.generic, customClass)}>
              <div className={styles.dialogContent}>
                <div className={styles.dialogHeader}>
                  <h3>{header}</h3>
                  {showCloseIcon && (
                    <IconButton
                      data-testid="dialog-close-action"
                      onClick={() => toggleDialog()}
                      className={styles.dialogClose}
                      variant="secondary"
                      icon={CloseIcon}
                    />
                  )}
                </div>
                <div className={styles.dialogBody}>{children}</div>
                {(cancelButtonLabel || actionButtonLabel) && (
                  <div className={styles.dialogFooter}>
                    <div className={styles.buttonGroup}>
                      <Button.Group>
                        {cancelButtonLabel && (
                          <Button
                            onClick={() => {
                              if (cancelAction) {
                                cancelAction()
                              } else {
                                toggleDialog()
                              }
                            }}
                            disabled={actionInProgress}
                            variant={closeButtonVariant}
                          >
                            {cancelButtonLabel}
                          </Button>
                        )}
                        {actionButtonLabel && (
                          <Button
                            data-testid="action-button"
                            onClick={e => doCallback(e)}
                            disabled={actionDisabled || actionInProgress}
                            variant="primary"
                          >
                            {actionButtonLabel}
                          </Button>
                        )}
                      </Button.Group>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </IdsProvider>
        </TdsProvider>
      </FocusLock>
    </Portal>
  )
}
